export interface Activity {
  id: string;
  name: string;
  type: string;
  start_date_local: string;
  distance: number | null;
  moving_time: number;
  total_elevation_gain: number | null;
  average_heartrate: number | null;
  max_heartrate: number | null;
  icu_training_load: number | null;
  icu_ctl: number | null;
  icu_atl: number | null;
  icu_weighted_avg_watts: number | null;
  icu_decoupling: number | null;
  icu_lthr: number | null;
  icu_ftp: number | null;
  icu_hr_zone_times: number[] | null;
}

export interface CalendarEvent {
  id: number;
  name: string;
  description: string | null;
  start_date_local: string;
  category: string | null;
  load?: number | null;       // explicit planned TSS (set in intervals.icu)
  duration?: number | null;   // planned duration in seconds
  type?: string | null;       // activity type: Ride, Run, etc.
}

export interface Wellness {
  ctl: number | null;
  atl: number | null;
  restingHR: number | null;
  hrv: number | null;
}

export interface WellnessDay {
  id: string;
  ctl: number | null;
  atl: number | null;
  hrv: number | null;
  restingHR: number | null;
}

export interface TrainingData {
  wellness: Wellness;
  wellnessHistory: WellnessDay[];
  activities: Activity[];
  events: CalendarEvent[];
}

// ── Training Planner ──────────────────────────────────────────────────────────

// Single source of truth for sports. SportType is derived from this, and the
// label/abbrev/category maps (plannerProfile, planGenerator, the view) all read
// it — add a sport here and it flows everywhere. The `category` drives which
// engine plans it: "endurance" → CTL/TSS aerobic ramp; "explosive" → the
// strength/power/anaerobic capacity engine (alpine, gym, etc.).
export type SportCategory = "endurance" | "explosive";

export const SPORTS = [
  // ── Endurance (aerobic, sustained) ──
  { value: "Run",          label: "Löpning",        abbrev: "Löp",     category: "endurance" },
  { value: "TrailRun",     label: "Terränglöpning", abbrev: "Trail",   category: "endurance" },
  { value: "Ride",         label: "Cykling",        abbrev: "Cykel",   category: "endurance" },
  { value: "MountainBike", label: "Mountainbike",   abbrev: "MTB",     category: "endurance" },
  { value: "GravelRide",   label: "Gruscykling",    abbrev: "Grus",    category: "endurance" },
  { value: "Swim",         label: "Simning",        abbrev: "Sim",     category: "endurance" },
  { value: "NordicSki",    label: "Längdskidor",    abbrev: "Längd",   category: "endurance" },
  { value: "RollerSki",    label: "Rullskidor",     abbrev: "Rullskidor", category: "endurance" },
  { value: "SkiTouring",   label: "Topptur",        abbrev: "Topp",    category: "endurance" },
  { value: "Rowing",       label: "Rodd",           abbrev: "Rodd",    category: "endurance" },
  { value: "Kayak",        label: "Kajak",          abbrev: "Kajak",   category: "endurance" },
  { value: "SUP",          label: "SUP",            abbrev: "SUP",     category: "endurance" },
  { value: "Hiking",       label: "Vandring",       abbrev: "Vandr",   category: "endurance" },
  { value: "Walk",         label: "Promenad",       abbrev: "Gång",    category: "endurance" },
  { value: "InlineSkate",  label: "Inlines",        abbrev: "Inlines", category: "endurance" },
  { value: "Orienteering", label: "Orientering",    abbrev: "OL",      category: "endurance" },
  { value: "Triathlon",    label: "Triathlon",      abbrev: "Tri",     category: "endurance" },
  // ── Explosive (strength / power / anaerobic / skill → capacity engine) ──
  { value: "AlpineSki",    label: "Utförsåkning",   abbrev: "Alpint",  category: "explosive" },
  { value: "Snowboard",    label: "Snowboard",      abbrev: "Snb",     category: "explosive" },
  { value: "WeightTraining", label: "Gym",          abbrev: "Gym",     category: "explosive" },
  { value: "Crossfit",     label: "Crossfit",       abbrev: "CF",      category: "explosive" },
  { value: "IceSkating",   label: "Skridsko",       abbrev: "Skrid",   category: "explosive" },
  { value: "Climbing",     label: "Klättring",      abbrev: "Klättr",  category: "explosive" },
  { value: "Bouldering",   label: "Bouldering",     abbrev: "Boulder", category: "explosive" },
  { value: "Football",     label: "Fotboll",        abbrev: "Fotb",    category: "explosive" },
  { value: "Floorball",    label: "Innebandy",      abbrev: "Innb",    category: "explosive" },
  { value: "IceHockey",    label: "Ishockey",       abbrev: "Hockey",  category: "explosive" },
  { value: "Handball",     label: "Handboll",       abbrev: "Handb",   category: "explosive" },
  { value: "Basketball",   label: "Basket",         abbrev: "Basket",  category: "explosive" },
  { value: "Volleyball",   label: "Volleyboll",     abbrev: "Volley",  category: "explosive" },
  { value: "Tennis",       label: "Tennis",         abbrev: "Tennis",  category: "explosive" },
  { value: "Padel",        label: "Padel",          abbrev: "Padel",   category: "explosive" },
  { value: "Badminton",    label: "Badminton",      abbrev: "Badm",    category: "explosive" },
  { value: "Golf",         label: "Golf",           abbrev: "Golf",    category: "explosive" },
  { value: "Yoga",         label: "Yoga",           abbrev: "Yoga",    category: "explosive" },
  { value: "Pilates",      label: "Pilates",        abbrev: "Pilates", category: "explosive" },
] as const;

export type SportType = typeof SPORTS[number]["value"];

export const SPORT_CATEGORY_LABELS: Record<SportCategory, string> = {
  endurance: "Uthållighet",
  explosive: "Explosivt",
};

export interface RaceGoal {
  raceName: string;
  raceDate: string;   // "YYYY-MM-DD"
  goalTime: string;   // free text, e.g. "3:45:00", "sub-4h", "topp-10"
  eventId?: number;
}

export interface PreferredDays {
  long: number[];  // 0 = Mon … 6 = Sun, 1–2 days
  hard: number[];  // 1–3 days
  easy: number[];  // 1–5 days
  gym:  number[];  // independent of endurance plan
}

export interface TrainingAvailability {
  maxWeeklyHours: number;
  preferredDays: PreferredDays;
}

export interface CommuteConfig {
  sport: "Run" | "Ride";
  minutesOneWay: number;
  days: number[]; // 0 = Mon … 6 = Sun
  startMonth?: number; // first month you commute (1-12); omit = all year
  endMonth?: number;   // last month you commute (1-12); wraps year-end
}

// A non-negotiable recurring commitment the plan must build around (e.g. a
// fixed lunch run with friends, a gym slot, a biweekly spinning class).
export interface FixedSession {
  id: string;
  day: number;          // 0 = Mon … 6 = Sun
  sport: SportType;
  durationMin: number;
  kind: "easy" | "quality" | "gym"; // easy/quality count as endurance volume; gym is strength
  biweekly?: boolean;   // every other week instead of weekly
  label?: string;       // e.g. "Lunchlöpning med polarna"
}

// A date range where the athlete can't train (vacation, work trip, etc.).
export interface BlockedRange {
  id: string;
  start: string;  // "YYYY-MM-DD" inclusive
  end: string;    // "YYYY-MM-DD" inclusive
  label?: string;
}

export interface PlannerProfile {
  sportsEnjoyed: SportType[];
  workoutLibrary: Partial<Record<SportType, string[]>>;
  raceGoals: RaceGoal[];
  goals: string;
  preferredDays: PreferredDays;
  maxWeeklyHours: number;
  planningMode?: "tss" | "duration";
  allowDoubles?: boolean; // permit a second easy session per day to reach higher volume
  doubleThreshold?: boolean; // Norwegian double-threshold day (advanced; needs a base)
  primarySport?: SportType; // main sport — kept in view via easy cross-training across blocks
  snowStartMonth?: number; // month snow arrives (1-12), default 11
  snowEndMonth?: number;   // month snow leaves (1-12), default 4
  fixedSessions?: FixedSession[]; // non-negotiable recurring commitments
  blockedRanges?: BlockedRange[]; // vacation / unavailable date ranges
  sickSince?: string | null;      // set while currently sick (start date); cleared on recovery
  commute?: CommuteConfig;
  setupComplete: boolean;
  createdAt: string;
  updatedAt: string;
}
export type TrainingPhase = "base" | "build" | "cutback" | "peak" | "taper" | "race";

// Race distance buckets that drive the distance profile (volume, long-run cap,
// interval focus). Defined here so RaceTarget can carry an explicit override;
// workoutBank re-exports this as its RaceDistance.
export type RaceDistance = "5k" | "10k" | "half" | "marathon" | "ultra" | "unknown";

export interface RaceTarget {
  name: string;
  date: string;       // "YYYY-MM-DD" — race day, or for a competition PERIOD its start
  endDate?: string;   // "YYYY-MM-DD" — period end; plan peaks to `date` and holds form across [date, endDate] (non-endurance goals)
  sport: SportType;
  kind?: "ironman";   // a Svenska Skidförbundet IronMan fystest goal (capacity engine, per-event targeting)
  eventId?: number;   // set if sourced from an existing intervals.icu event
  priority?: "A" | "B" | "C"; // A = season target, B/C = tune-up races
  distance?: RaceDistance;    // explicit band override (legacy/explicit)
  distanceKm?: number;        // race distance in km — scales the plan continuously
  durationHours?: number;     // expected finishing time in hours — sizes the long run (wins over km)
  trail?: boolean;            // terrain race → hill emphasis + time-on-feet long runs
  goalTime?: string;          // free-text target time, e.g. "3:45" — parsed into durationHours
}

export interface PlannedWeek {
  weekKey: string;    // "YYYY-Www"
  label: string;      // "V22"
  targetTSS: number;
  targetHours?: number;
  phase: TrainingPhase;
}

export interface PlannedSession {
  name: string;           // "Long Z2", "Threshold", "Easy"
  date: string;           // "YYYY-MM-DD"
  weekKey: string;        // "YYYY-Www" — ISO week key matching PlannedWeek.weekKey
  sport: SportType;
  durationHours: number;
  targetIF: number;
  estimatedTSS: number;
  description: string;    // "1h 45min Z2 aerobic run"
  noPublish?: boolean;    // counted for volume but not pushed to intervals.icu (e.g. commute)
}
